/**
 * Scheduling slot math, ported (copied + slimmed) from the legacy
 * timeSlotUtils. The retired Email mode's after-hours window is gone; slots
 * are plain office hours. All times are HH:MM strings on a single day.
 */

import type { CalendarEvent } from '@/lib/api/calendar-api';
import { SLOTS, staffByColorId } from './config';

export function toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export function toTimeString(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function formatTime12h(time: string): string {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
}

export function formatDateLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** All bookable slot start times (08:00 … 20:45) */
export function generateSlots(): string[] {
  const slots: string[] = [];
  for (let hour = SLOTS.START_HOUR; hour <= SLOTS.END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += SLOTS.INCREMENT_MINUTES) {
      if (hour === SLOTS.END_HOUR && minute > SLOTS.END_MINUTE) break;
      slots.push(toTimeString(hour * 60 + minute));
    }
  }
  return slots;
}

export function isSlotInPast(time: string, date: Date): boolean {
  const now = new Date();
  if (formatDateLocal(date) !== formatDateLocal(now)) return false;
  return toMinutes(time) < now.getHours() * 60 + now.getMinutes();
}

/** Employee IDs (profiles UUIDs) an event occupies, using color_id fallback */
function eventEmployeeIds(
  event: CalendarEvent,
  emailToProfileId: Record<string, string>
): string[] {
  const ids = Array.isArray(event.employee_ids)
    ? event.employee_ids
    : event.employee_id
      ? [event.employee_id]
      : [];
  if (ids.length > 0) return ids;
  const staff = staffByColorId(event.color_id);
  const profileId = staff?.email ? emailToProfileId[staff.email] : undefined;
  return profileId ? [profileId] : [];
}

/** True when [time, time+duration) is clear for the employee on that date */
export function isSlotAvailable(
  time: string,
  profileId: string,
  durationMinutes: number,
  date: Date,
  events: CalendarEvent[],
  emailToProfileId: Record<string, string>
): boolean {
  const dateStr = formatDateLocal(date);
  const slotStart = toMinutes(time);
  const slotEnd = slotStart + durationMinutes;

  return !events.some(event => {
    if (event.event_date !== dateStr) return false;
    if (!event.start_time || !event.end_time) return false;
    if (!eventEmployeeIds(event, emailToProfileId).includes(profileId)) {
      return false;
    }
    const eventStart = toMinutes(event.start_time);
    const eventEnd = toMinutes(event.end_time);
    return slotStart < eventEnd && slotEnd > eventStart;
  });
}

/**
 * The clicked-slot heuristic from V1: for multi-slot durations, keep the
 * clicked slot as the start when the following slots are free; otherwise
 * shift backward so the clicked slot becomes the last one — but only when
 * the whole shifted range is free and starts within office hours (a blind
 * shift could double-book the earlier slots).
 */
export function resolveStartTime(
  clickedTime: string,
  profileId: string,
  durationMinutes: number,
  date: Date,
  events: CalendarEvent[],
  emailToProfileId: Record<string, string>
): string {
  if (durationMinutes === 15) return clickedTime;
  const clicked = toMinutes(clickedTime);
  const slotsNeeded = durationMinutes / 15;

  for (let i = 1; i < slotsNeeded; i++) {
    const nextSlot = toTimeString(clicked + i * 15);
    if (
      !isSlotAvailable(nextSlot, profileId, 15, date, events, emailToProfileId)
    ) {
      const shiftedStart = clicked - (durationMinutes - 15);
      const shiftedTime = toTimeString(shiftedStart);
      if (
        shiftedStart >= SLOTS.START_HOUR * 60 &&
        isSlotAvailable(
          shiftedTime,
          profileId,
          durationMinutes,
          date,
          events,
          emailToProfileId
        )
      ) {
        return shiftedTime;
      }
      return clickedTime;
    }
  }
  return clickedTime;
}
